import React, { useEffect, useMemo, useState } from "react";
import { FaEye, FaPlus, FaUserCog } from "react-icons/fa";
import styles from "../../master/academicConfiguration/AcademicCreate.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const StudentTimeList = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";

  /* ===========================
     LANGUAGE (MUST BE FIRST)
  =========================== */
  const { translations } = useSelector((state) => state.language);
  const navigate = useNavigate(); // ✅ hook at top level

  /* ===========================
     STATES
  =========================== */
  const [classValue, setClassValue] = useState("");

  const [sectionValue, setSectionValue] = useState("");

  const [shiftValue, setShiftValue] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50, // timetable usually needs bigger page size
  });
  const [data, setData] = useState([]);
  const [sectionList, setSectionList] = useState([]);

  const [shiftList, setShiftList] = useState([]);
  const [errors, setErrors] = useState({});
  // API call to fetch class list
  const fetchClassList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/classList`, {
        id: school_id,
        academic_year: academic_year,
      });

      if (response?.data?.success) {
        const apiData = response.data.data || [];

        // Transform API data to match table structure
        const transformedData = apiData
          .filter((item) => item.domain_id !== "CLASS")
          .map((item) => ({
            id: item.id,
            class_id: item.class_id,
            class: item.domain_id,
            label: item.domain_id,
            value: item.class_id,
            maleCount: item.no_of_boys || 0,
            femaleCount: item.no_of_girls || 0,
            totalStudents: (item.no_of_boys || 0) + (item.no_of_girls || 0),
            noOfSection: item.no_of_section || 0,
          }));

        setData(transformedData);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchSectionList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        `/school/timeTable/sectionDropdown `,
        {
          id: [classValue.class_id],
          school_id: school_id,
        },
      );

      if (response?.data?.status) {
        const apiData = response.data.data || [];
        const transformedData = apiData.map((item) => ({
          id: item.id,
          class_id: item.class_id,
          section: item.section_id,
          label: item.section_id,
          value: item.section_id,
        }));

        setSectionList(transformedData);
      } else {
        setSectionList([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setSectionList([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchShiftList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/shiftList/${school_id}`,
      );
      if (response?.data?.status) {
        const apiData = response.data.data || [];
        // Transform API data to match table structure
        const transformedData = apiData.map((item) => ({
          value: item.id,
          label: item.shift_type,
        }));
        setShiftList(transformedData);
      } else {
        setShiftList([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setShiftList([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (classValue) {
      fetchSectionList();
    }
  }, [classValue]);
  useEffect(() => {
    if (school_id) {
      fetchClassList();
      fetchShiftList();
    }
  }, [school_id]);

  /* ===========================
     FETCH TABLE DATA
  =========================== */
  useEffect(() => {
    if (classValue && sectionValue && shiftValue) {
      fetchDaySchedule();
    }
  }, [classValue, sectionValue, shiftValue]);

  const fetchDaySchedule = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/timeTable/scheduleList", {
        school_id: school_id,
        class_id: classValue.value,
        section: sectionValue.value,
        shift_id: shiftValue.value,
      });

      if (response?.data) {
        setTableData(response.data.data || []);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching day schedule", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };
  const nextDay = useMemo(() => {
    if (!Array.isArray(tableData) || tableData.length === 0) return 1;
    const maxDay = tableData.reduce(
      (max, item) => Math.max(max, Number(item.day) || 0),
      0,
    );
    return maxDay + 1;
  }, [tableData]);

  const validateForm = () => {
    const newErrors = {};
    if (!classValue) newErrors.classValue = translations.errors.classRequired;
    if (!sectionValue)
      newErrors.sectionValue = translations.errors.sectionRequired;
    if (!shiftValue) newErrors.shiftValue = translations.errors.shiftRequired;
    return newErrors;
  };

  const handleAdd = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    navigate("/user/CreateTimeTable", {
      state: {
        DAY: nextDay,
        CLASS: classValue,
        SECTION: sectionValue,
        SHIFT: shiftValue,
      },
    });
  };

  /* ===========================
     TABLE COLUMNS (DYNAMIC)
  =========================== */

  const handleView = (rowData) => {
    const { day } = rowData;

    // find any one record for this day (they all share same class/section/shift)
    const matchedRow = tableData.find((item) => item.day === day);

    if (!matchedRow) return;

    navigate("/user/CreateTimeTable", {
      state: {
        DAY: matchedRow.day,
        // Reuse the already-selected (and already-labelled) filter values
        // instead of raw ids, so the create page can show them right away
        // without waiting on its own class/shift lists to load.
        CLASS: classValue,
        SECTION: sectionValue,
        SHIFT: shiftValue,
        VIEW: true,
      },
    });
  };

  const { formattedData, dynamicPeriods } = useMemo(() => {
    const grouped = {};
    const periodSet = new Set();
    tableData.forEach((item) => {
      const { day, period, subject_name } = item;
      periodSet.add(period); // collect all unique periods
      if (!grouped[day]) {
        grouped[day] = { day };
      }
      grouped[day][`period_${period}`] = subject_name;
    });
    return {
      formattedData: Object.values(grouped),
      dynamicPeriods: Array.from(periodSet).sort((a, b) => a - b),
    };
  }, [tableData]);

  const columns = useMemo(() => {
    if (!dynamicPeriods.length) return [];
    const periodColumns = dynamicPeriods.map((period) => ({
      accessorKey: `period_${period}`,
      header: `Period ${period}`,
      enableSorting: false,
    }));
    return [
      {
        accessorKey: "day",
        header: "Day / Period",
        enableSorting: false,
      },
      ...periodColumns,
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.viewStudent || "View Student"}
          >
            <FaEye />
          </button>
        ),
      },
    ];
  }, [dynamicPeriods]);
  const handleDropdownChange = (field, value, setter) => {
    setter(value);
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    // 👇 RESET DEPENDENT DROPDOWNS
    if (field === "classValue") {
      setSectionValue("");
      setSectionList([]);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaUserCog />
        </div>
        <h1 className={styles.headerTitle}>
          {" "}
          {translations?.labels?.dayScheduleListTitle}
        </h1>
      </div>
      {/* ACTIONS */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleAdd}
          fullWidth={false}
        >
          <FaPlus style={{ marginRight: "8px" }} />
          {translations?.labels?.add}
        </CustomButton>
      </div>
      {/* FILTER CLASS */}

      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.classLabel}
            value={classValue}
            options={data}
            onChange={(value) =>
              handleDropdownChange("classValue", value, setClassValue)
            }
            placeholder={translations?.labels?.classPlaceholder}
            required
            error={errors.classValue}
          />
        </div>
        {/* FILTER SECTION */}

        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.sectionLabel}
            value={sectionValue}
            options={sectionList}
            onChange={(value) =>
              handleDropdownChange("sectionValue", value, setSectionValue)
            }
            placeholder={translations?.labels?.sectionPlaceholder}
            required
            error={errors.sectionValue}
          />
        </div>
        {/* FILTER SHIFT */}

        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.shiftLabel}
            value={shiftValue}
            options={shiftList}
            onChange={(value) =>
              handleDropdownChange("shiftValue", value, setShiftValue)
            }
            placeholder={translations?.labels?.shiftPlaceholder}
            required
            error={errors.shiftValue}
          />
        </div>
      </div>
      {/* ===========================
          TABLE (LOAD AFTER SELECT)
      =========================== */}
      {classValue && sectionValue && shiftValue && (
        <CustomMRTTable
          columns={columns}
          data={formattedData}
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowCount={formattedData.length}
          enablePagination={false}
          enableSearch={false}
          enableFiltering={false}
        />
      )}
    </div>
  );
};

export default StudentTimeList;
